/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * DATA ACCESS LAYER : video_topics
 * ═══════════════════════════════════════════════════════════════════════════════
 * 
 * Ce fichier gère les opérations CRUD sur la table `video_topics`.
 * Les sujets sont créés en sandbox par les PBM puis validés pour créer des video_content.
 * 
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { supabase } from "@/integrations/supabase/client";
import { createVideoContent } from "./videoContent";
import { createTopicFolders } from "./rushesVirtualFolders";

// Types basés sur la structure de la table video_topics
export interface VideoTopic {
  id: string;
  user_id: string;
  shooting_id: string | null;
  title: string;
  description: string | null;
  status: 'sandbox' | 'validated';
  funnel_type: 'tofu' | 'bofu';
  created_by: string;
  validated_at: string | null;
  validated_by: string | null;
  video_content_id: string | null;
  source: 'manual' | 'ai';
  sections: { label: string; content: string }[] | null;
  editorial_type: string | null;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface VideoTopicInsert {
  user_id: string;
  title: string;
  description?: string | null;
  funnel_type?: 'tofu' | 'bofu';
  created_by: string;
  source?: 'manual' | 'ai';
}

export interface VideoTopicUpdate {
  title?: string;
  description?: string | null;
  funnel_type?: 'tofu' | 'bofu';
  shooting_id?: string | null;
  status?: 'sandbox' | 'validated';
  validated_at?: string | null;
  validated_by?: string | null;
  video_content_id?: string | null;
}

/**
 * Get topics for a specific user, optionally filtered by status
 */
export const getTopicsForUser = async (
  userId: string, 
  status?: 'sandbox' | 'validated'
): Promise<{ success: boolean; data?: VideoTopic[]; error?: unknown }> => {
  try {
    let query = supabase
      .from("video_topics")
      .select("*")
      .eq("user_id", userId)
      .order("order_index", { ascending: true });

    if (status) {
      query = query.eq("status", status);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { success: true, data: data as VideoTopic[] };
  } catch (error) {
    console.error("Error fetching video topics:", error);
    return { success: false, error };
  }
};

/**
 * Get topics for a specific shooting
 */
export const getTopicsForShooting = async (
  shootingId: string
): Promise<{ success: boolean; data?: VideoTopic[]; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("video_topics")
      .select("*")
      .eq("shooting_id", shootingId)
      .eq("status", "validated")
      .order("validated_at", { ascending: true });

    if (error) throw error;
    return { success: true, data: data as VideoTopic[] };
  } catch (error) {
    console.error("Error fetching topics for shooting:", error);
    return { success: false, error };
  }
};

/**
 * Create a new topic in sandbox
 */
export const createTopic = async (
  topic: VideoTopicInsert
): Promise<{ success: boolean; data?: VideoTopic; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("video_topics")
      .insert({
        ...topic,
        status: 'sandbox',
        source: topic.source || 'manual',
      })
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data as VideoTopic };
  } catch (error) {
    console.error("Error creating video topic:", error);
    return { success: false, error };
  }
};

/**
 * Update a topic
 */
export const updateTopic = async (
  id: string,
  updates: VideoTopicUpdate
): Promise<{ success: boolean; data?: VideoTopic; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("video_topics")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data: data as VideoTopic };
  } catch (error) {
    console.error("Error updating video topic:", error);
    return { success: false, error };
  }
};

/**
 * Unvalidate a topic: revert status to sandbox
 */
export const unvalidateTopic = async (
  id: string
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    const { error } = await supabase
      .from("video_topics")
      .update({ status: "sandbox", validated_at: null, validated_by: null })
      .eq("id", id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error unvalidating video topic:", error);
    return { success: false, error };
  }
};

/**
 * Delete a topic (only for sandbox topics)
 */
export const deleteTopic = async (
  id: string
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    const { error } = await supabase
      .from("video_topics")
      .delete()
      .eq("id", id)
      .eq("status", "sandbox"); // Safety: only delete sandbox topics

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error("Error deleting video topic:", error);
    return { success: false, error };
  }
};

/**
 * Force-delete a topic regardless of status.
 * For validated topics, also cleans up the associated video_content.
 */
export const deleteTopicForce = async (
  id: string
): Promise<{ success: boolean; error?: unknown }> => {
  try {
    // Fetch topic to check for video_content_id
    const { data: topic } = await supabase
      .from("video_topics")
      .select("video_content_id")
      .eq("id", id)
      .single();

    // Delete the topic (no status filter)
    const { error } = await supabase
      .from("video_topics")
      .delete()
      .eq("id", id);

    if (error) throw error;

    // Clean up associated video_content if any
    if (topic?.video_content_id) {
      await supabase
        .from("video_content")
        .delete()
        .eq("id", topic.video_content_id);
    }

    return { success: true };
  } catch (error) {
    console.error("Error force-deleting video topic:", error);
    return { success: false, error };
  }
};

/**
 * Get the next shooting for a user (to auto-assign validated topics)
 */
export const getNextShootingForUser = async (
  userId: string
): Promise<{ success: boolean; data?: { id: string; date: string }; error?: unknown }> => {
  try {
    const now = new Date().toISOString();
    
    const { data, error } = await supabase
      .from("bookings")
      .select(`
        shooting_id,
        start_time,
        shootings!inner(id, date)
      `)
      .eq("user_id", userId)
      .gt("start_time", now)
      .not("shooting_id", "is", null)
      .order("start_time", { ascending: true })
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    
    if (!data?.shooting_id) {
      return { success: true, data: undefined };
    }

    return { 
      success: true, 
      data: { 
        id: data.shooting_id, 
        date: data.start_time 
      } 
    };
  } catch (error) {
    console.error("Error fetching next shooting:", error);
    return { success: false, error };
  }
};

/**
 * Validate a topic: creates video_content and updates the topic
 */
export const validateTopic = async (
  topicId: string,
  validatedBy: string,
  targetShootingId?: string | null
): Promise<{ success: boolean; data?: VideoTopic; error?: unknown }> => {
  try {
    // 1. Fetch the topic
    const { data: topic, error: topicError } = await supabase
      .from("video_topics")
      .select("*")
      .eq("id", topicId)
      .single();

    if (topicError) throw topicError;
    if (!topic) throw new Error("Topic not found");
    if (topic.status === 'validated') {
      return { success: false, error: new Error("Topic already validated") };
    }

    // 2. Use provided shooting or fallback to next upcoming
    let shootingId = targetShootingId ?? null;
    if (!shootingId) {
      const nextShooting = await getNextShootingForUser(topic.user_id);
      shootingId = nextShooting.data?.id || null;
    }

    // 3. Create video_content FIRST (we need the ID for folder naming)
    const videoResult = await createVideoContent({
      user_id: topic.user_id,
      title: topic.title,
      description: topic.description || undefined,
      shooting_id: shootingId,
      status: 'shot',
      is_prsnl_content: true,
      video_1_url: '', // Required field, will be populated later
    });

    if (!videoResult.success || !videoResult.data) {
      throw new Error("Failed to create video content");
    }

    const videoContentId = videoResult.data.id;

    // 4. Create topic folders with embedded video content ID
    let userAssetsFolderId: string | null = null;
    if (shootingId) {
      const foldersResult = await createTopicFolders(shootingId, topic.title, videoContentId);
      if (foldersResult.success && foldersResult.rushesFolderId) {
        userAssetsFolderId = foldersResult.rushesFolderId;
        console.log(`[validateTopic] Created topic folders. Rushes folder ID: ${userAssetsFolderId}`);
        
        // Update video_content with the folder link
        await supabase
          .from('video_content')
          .update({ user_assets_folder_id: userAssetsFolderId })
          .eq('id', videoContentId);
      } else {
        console.warn(`[validateTopic] Could not create topic folders: ${foldersResult.error}`);
      }
    }

    // 5. Update the topic
    const { data: updatedTopic, error: updateError } = await supabase
      .from("video_topics")
      .update({
        status: 'validated',
        shooting_id: shootingId,
        video_content_id: videoResult.data.id,
        validated_at: new Date().toISOString(),
        validated_by: validatedBy,
      })
      .eq("id", topicId)
      .select()
      .single();

    if (updateError) throw updateError;

    return { success: true, data: updatedTopic as VideoTopic };
  } catch (error) {
    console.error("Error validating topic:", error);
    return { success: false, error };
  }
};

/**
 * Revert a validated topic back to sandbox
 * Clears shooting_id, video_content_id, and resets validation fields
 */
export const revertTopicToSandbox = async (
  topicId: string
): Promise<{ success: boolean; data?: VideoTopic; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("video_topics")
      .update({
        status: 'sandbox',
        shooting_id: null,
        video_content_id: null,
        validated_at: null,
        validated_by: null,
      })
      .eq("id", topicId)
      .select()
      .single();

    if (error) throw error;
    console.log(`[revertTopicToSandbox] Topic ${topicId} reverted to sandbox`);
    return { success: true, data: data as VideoTopic };
  } catch (error) {
    console.error("Error reverting topic to sandbox:", error);
    return { success: false, error };
  }
};

/**
 * Find orphan topics: validated topics whose video_content no longer exists
 * Used after shooting simulation to return unfilmed topics to sandbox
 */
export const findOrphanTopicsForShooting = async (
  shootingId: string
): Promise<{ success: boolean; data?: VideoTopic[]; error?: unknown }> => {
  try {
    // Get all validated topics for this shooting
    const { data: topics, error: topicsError } = await supabase
      .from("video_topics")
      .select("*")
      .eq("shooting_id", shootingId)
      .eq("status", "validated");

    if (topicsError) throw topicsError;
    if (!topics || topics.length === 0) {
      return { success: true, data: [] };
    }

    // Get existing video_content IDs for this shooting
    const { data: videoContents, error: vcError } = await supabase
      .from("video_content")
      .select("id")
      .eq("shooting_id", shootingId);

    if (vcError) throw vcError;

    const existingVideoIds = new Set((videoContents || []).map(vc => vc.id));

    // Find topics whose video_content_id doesn't exist anymore
    const orphanTopics = topics.filter(topic => 
      topic.video_content_id && !existingVideoIds.has(topic.video_content_id)
    );

    console.log(`[findOrphanTopicsForShooting] Found ${orphanTopics.length} orphan topics out of ${topics.length} total`);
    return { success: true, data: orphanTopics as VideoTopic[] };
  } catch (error) {
    console.error("Error finding orphan topics:", error);
    return { success: false, error };
  }
};

export interface PbmAssignedUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

/**
 * Get users assigned to a PBM
 * For admins/super_admins not in team_personal_brand_managers, returns all customers with a PBM
 */
export const getUsersAssignedToPbm = async (
  pbmUserId: string
): Promise<{ success: boolean; data?: PbmAssignedUser[]; error?: unknown }> => {
  try {
    // First get the team_personal_brand_managers ID for this user
    const { data: pbmData, error: pbmError } = await supabase
      .from("team_personal_brand_managers")
      .select("id")
      .eq("user_id", pbmUserId)
      .maybeSingle();

    if (pbmError) throw pbmError;
    
    // If user is not a registered PBM, check if they're an admin
    if (!pbmData) {
      // Check if user is admin or super_admin
      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", pbmUserId)
        .in("role", ["admin", "super_admin"]);
      
      const isAdmin = roleData && roleData.length > 0;
      
      if (isAdmin) {
        // For admins, return all active customers (no filtering by PBM assignment)
        const { data: usersData, error: usersError } = await supabase
          .from("users")
          .select("id, email, first_name, last_name, avatar_url")
          .eq("is_active", true)
          .order("first_name", { ascending: true });

        if (usersError) throw usersError;
        return { success: true, data: (usersData || []) as unknown as PbmAssignedUser[] };
      }
      
      return { success: true, data: [] };
    }

    // PBMs see ALL active customers
    const { data: usersData, error: usersError } = await supabase
      .from("users")
      .select("id, email, first_name, last_name, avatar_url")
      .eq("is_active", true)
      .order("first_name", { ascending: true });

    if (usersError) throw usersError;

    // Cast to our expected type
    const users = (usersData || []) as unknown as PbmAssignedUser[];

    return { success: true, data: users };
  } catch (error) {
    console.error("Error fetching PBM assigned users:", error);
    return { success: false, error };
  }
};

/**
 * Get a single topic by ID
 */
export const getTopicById = async (
  topicId: string
): Promise<{ success: boolean; data?: VideoTopic; error?: unknown }> => {
  try {
    const { data, error } = await supabase
      .from("video_topics")
      .select("*")
      .eq("id", topicId)
      .single();

    if (error) throw error;
    return { success: true, data: data as VideoTopic };
  } catch (error) {
    console.error("Error fetching topic:", error);
    return { success: false, error };
  }
};

/**
 * Revert validated topics that have no section content back to sandbox.
 * A topic is considered "empty" if none of its sections have trimmed content.
 */
export const revertEmptyValidatedTopics = async (
  userId: string
): Promise<{ success: boolean; revertedCount: number; error?: unknown }> => {
  try {
    const { data: topics, error: fetchError } = await supabase
      .from("video_topics")
      .select("id, sections")
      .eq("user_id", userId)
      .eq("status", "validated");

    if (fetchError) throw fetchError;
    if (!topics || topics.length === 0) return { success: true, revertedCount: 0 };

    const emptyIds = topics.filter(t => {
      if (!t.sections || !Array.isArray(t.sections)) return true;
      const sections = t.sections as { label: string; content: string }[];
      return !sections.some(s => s.content?.trim());
    }).map(t => t.id);

    if (emptyIds.length === 0) return { success: true, revertedCount: 0 };

    const { error: updateError } = await supabase
      .from("video_topics")
      .update({
        status: 'sandbox',
        shooting_id: null,
        video_content_id: null,
        validated_at: null,
        validated_by: null,
      })
      .in("id", emptyIds);

    if (updateError) throw updateError;

    console.log(`[revertEmptyValidatedTopics] Reverted ${emptyIds.length} empty topics to sandbox`);
    return { success: true, revertedCount: emptyIds.length };
  } catch (error) {
    console.error("Error reverting empty validated topics:", error);
    return { success: false, revertedCount: 0, error };
  }
};
